import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../layout/Icon.jsx';
import { useDebounced, useGlobalSearch, MIN_CHARS } from './useGlobalSearch.js';
import { resultPath, resultTitle, resultSubtitle } from './resultMeta.js';

/**
 * مربع البحث العام في الشريط العلوي.
 *
 * نتيجة واحدة مسطّحة خلف الكواليس (flat) حتى تعمل الأسهم عبر المجموعات،
 * وعرضٌ مجمَّع حسب النوع أمام المستخدم.
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);

  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const debounced = useDebounced(term);
  const ready = debounced.trim().length >= MIN_CHARS;
  const { data, isFetching, isError } = useGlobalSearch(debounced);

  // نُبقي نتائج الكلمة السابقة ظاهرة أثناء جلب التالية (keepPreviousData)
  // فلا تومض القائمة مع كل حرف.
  const groups = useMemo(() => (data && Array.isArray(data.groups) ? data.groups : []), [data]);

  // قائمة مسطّحة بترتيب العرض — عليها تتحرّك الأسهم.
  const flat = useMemo(
    () => groups.flatMap((g) => g.items.map((item) => ({ groupKey: g.key, item }))),
    [groups]
  );

  useEffect(() => { setCursor(-1); }, [debounced]);

  // إغلاق عند النقر خارج المربع.
  useEffect(() => {
    function onDown(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // إبقاء السطر المحدَّد ظاهراً داخل القائمة.
  useEffect(() => {
    if (cursor < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${cursor}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const choose = (entry) => {
    if (!entry) return;
    setOpen(false);
    setCursor(-1);
    if (inputRef.current) inputRef.current.blur();
    navigate(resultPath(entry.groupKey, entry.item));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (!flat.length) {
      if (e.key === 'ArrowDown') setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setCursor((c) => (c + 1 >= flat.length ? flat.length - 1 : c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (c <= 0 ? 0 : c - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(flat[cursor >= 0 ? cursor : 0]);
    }
  };

  const clear = () => {
    setTerm('');
    setCursor(-1);
    setOpen(false);
    if (inputRef.current) inputRef.current.focus();
  };

  const showPanel = open && term.trim().length > 0;
  const loading = ready && isFetching && !flat.length;
  const empty = ready && !isFetching && !flat.length && !isError;

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex items-center gap-2 bg-white border border-line rounded-[10px] px-3 py-2 min-w-[280px] shadow-card">
        <Icon name="search" size={16} color="#9CA3AF" />
        <input
          ref={inputRef}
          className="border-0 outline-none flex-1 bg-transparent text-[13px]"
          placeholder="ابحث عن طفل، رقم كارت، فاتورة..."
          value={term}
          onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          data-testid="global-search-input"
          role="combobox"
          aria-expanded={showPanel}
          aria-autocomplete="list"
          aria-controls="global-search-results"
        />
        {term ? (
          <button
            type="button"
            onClick={clear}
            title="مسح البحث"
            aria-label="مسح البحث"
            className="text-ink-3 hover:text-ink grid place-items-center"
          >
            <Icon name="x" size={14} />
          </button>
        ) : null}
      </div>

      {showPanel ? (
        <div
          id="global-search-results"
          role="listbox"
          ref={listRef}
          data-testid="global-search-panel"
          className="absolute top-[44px] w-[420px] card p-0 z-30 max-h-[460px] overflow-y-auto shadow-pop"
          style={{ insetInlineStart: 0 }}
        >
          {!ready ? (
            <div className="px-4 py-6 text-center text-ink-3 text-xs">
              اكتب حرفين على الأقل للبحث
            </div>
          ) : null}

          {loading ? (
            <div className="px-4 py-6 text-center text-ink-3 text-xs" data-testid="global-search-loading">
              جاري البحث...
            </div>
          ) : null}

          {isError && ready ? (
            <div className="px-4 py-6 text-center text-red text-xs">تعذّر تنفيذ البحث</div>
          ) : null}

          {empty ? (
            <div className="px-4 py-6 text-center text-ink-3 text-xs" data-testid="global-search-empty">
              لا توجد نتائج
            </div>
          ) : null}

          {flat.length ? (
            <div className="flex flex-col">
              {groups.map((group) => (
                <div key={group.key}>
                  <div className="px-4 py-1.5 bg-paper-2 border-y border-line flex items-center gap-1.5 sticky top-0">
                    <Icon name={group.icon} size={13} color="#9CA3AF" />
                    <span className="text-[11px] font-bold text-ink-2">{group.label}</span>
                    <span className="text-[10.5px] text-ink-3">({group.total})</span>
                  </div>
                  {group.items.map((item) => {
                    const idx = flat.findIndex((f) => f.groupKey === group.key && f.item.id === item.id
                      && f.item.kind === item.kind);
                    const activeRow = idx === cursor;
                    return (
                      <button
                        key={`${group.key}-${item.kind || ''}-${item.id}`}
                        type="button"
                        role="option"
                        aria-selected={activeRow}
                        data-idx={idx}
                        data-testid="global-search-result"
                        onMouseEnter={() => setCursor(idx)}
                        onClick={() => choose({ groupKey: group.key, item })}
                        className={`w-full text-right px-4 py-2 border-b border-line last:border-b-0 ${
                          activeRow ? 'bg-orange-soft' : 'hover:bg-paper-2'
                        }`}
                      >
                        <div className="text-[13px] font-semibold truncate">
                          {resultTitle(group.key, item)}
                        </div>
                        <div className="text-[11px] text-ink-3 truncate">
                          {resultSubtitle(group.key, item)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
              <div className="px-4 py-1.5 text-[10.5px] text-ink-3 border-t border-line">
                ↑ ↓ للتنقل · Enter للفتح · Esc للإغلاق
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
